import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import { Box, Button, IconButton, Toolbar } from "@mui/material";
import MuiAppBar, { AppBarProps } from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import ThemeSwitchButton from "@/components/buttons/ThemeSwitchButton";
import ToolbarHelpButton from "@/components/buttons/ToolbarHelpButton";
import Notifications from "@/components/notifications/Notifications";
import AccountMenu from "./AccountMenu";
import { usePostHog } from "posthog-js/react";
import useIntercom from "@/hooks/useIntercom";
import { PAGE_MAX_WIDTH } from "@/providers/DrawerProvider";

type StyledAppBarPropType = AppBarProps & {
  width?: number;
  open: boolean;
};

type FederatedPropType = {
  enabled: boolean;
  email: string | undefined;
  release: () => void;
};

type BaseAppBarPropType = {
  width: number;
  open: boolean;
  position: "fixed" | "sticky" | "absolute" | "static" | "relative";
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Set while the sidebar is collapsed to its icon rail (R-2295): the » button
   * that expands it again, in the menu button's place.
   */
  expandRail?: () => void;
  federatedMode: FederatedPropType;
};

const StyledAppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<StyledAppBarPropType>(({ theme, width }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        [theme.breakpoints.up("md")]: {
          width: `calc(100% - ${width}px)`,
          marginLeft: `${width}px`,
        },
        [theme.breakpoints.down("md")]: {
          width: "100%",
          marginLeft: 0,
        },
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const FederatedMode: React.FC<FederatedPropType> = ({
  enabled,
  email,
  release,
}) => {
  const posthog = usePostHog();
  const { shutdown } = useIntercom();
  const releaseUser = React.useCallback(() => {
    release();

    posthog && posthog.reset(true);
    // Intercom's cookie would otherwise carry the released user's session
    // into whatever page comes next, the marketing site's included (R-1873).
    shutdown();

    window.location.reload();
  }, [posthog, release, shutdown]);

  return enabled && email ? (
    <Box
      data-testid="federated-mode"
      sx={{
        py: 1,
        order: { xs: -1, lg: 0 },
        width: { xs: "100%", lg: "auto" },
      }}
    >
      <Button
        fullWidth
        color="warning"
        variant="outlined"
        onClick={releaseUser}
      >
        Release User {email}
      </Button>
    </Box>
  ) : null;
};

const BaseAppBar: React.FC<BaseAppBarPropType> = ({
  open,
  setOpen,
  width,
  position = "relative",
  expandRail,
  federatedMode,
}) => {
  const theme = useTheme();

  return (
    <StyledAppBar
      elevation={1}
      position={position}
      width={width}
      open={open}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Toolbar
        data-testid="app-toolbar"
        sx={{
          width: "100%",
          maxWidth: PAGE_MAX_WIDTH,
          flexWrap: {
            xs: "wrap",
          },
          justifyContent: "flex-start",
          alignItems: "stretch",
          py: 2,
        }}
      >
        <Box
          sx={{
            display: !open ? "flex" : "none",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <IconButton
            color="primary"
            aria-label="open drawer"
            onClick={() => setOpen((prev) => !prev)}
          >
            <MenuIcon />
          </IconButton>
        </Box>
        {open && expandRail ? (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {/* The « button mirrored, in the same colour, on the fixed App Bar
                so it stays in view while the page scrolls. */}
            <IconButton
              aria-label="expand sidebar"
              onClick={expandRail}
              sx={{ color: "action.active" }}
            >
              <KeyboardDoubleArrowRightIcon />
            </IconButton>
          </Box>
        ) : null}
        <FederatedMode
          enabled={federatedMode.enabled}
          email={federatedMode.email}
          release={federatedMode.release}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            ml: "auto",
          }}
        >
          <ThemeSwitchButton />
          <ToolbarHelpButton />
          <Notifications />
          <Box sx={{ ml: 1 }}>
            <AccountMenu />
          </Box>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
};

export default BaseAppBar;
